import { AlertTriangle, Sparkles } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { AiSurface } from '@/components/ui/ai-surface';
import { cn } from '@/lib/utils/cn';
import {
  describeBaseFormationRun,
  isRunInFlight,
  useLatestBaseFormationRunQuery,
} from '@/lib/api/base-formation';
import type { DocumentListItem } from '@/lib/api/documents';
import { progressLabel } from '@/lib/utils/processing';
import { nothingToReadNote } from './indexed-documents';

/**
 * The state of the engagement's latest AI extraction run, above the
 * documents library: reading (with real section progress and, when the AI
 * service is busy, that it is waiting), degraded (sections skipped),
 * nothing to read (the run found no document to read, T-0228) or failed,
 * each in plain words. Nothing is rendered when the engagement has never
 * run or the last run succeeded (the "Run AI extraction" control then links
 * to the proposals). Same AI-surface treatment as the queue banner.
 *
 * A run that found nothing to read is worded against the documents the
 * screen lists (`nothingToReadNote`): the run reads only indexed vendor and
 * Avero documents, so "no document has been indexed" is said only when none
 * is. Until the list has loaded that banner waits, rather than guess.
 */
export function ExtractionStatusBanner({
  engagementId,
  documents,
  className,
}: {
  engagementId: string;
  /** The documents the screen lists; undefined while the list loads. */
  documents: readonly Pick<DocumentListItem, 'process_status' | 'source' | 'purpose'>[] | undefined;
  className?: string;
}) {
  const latest = useLatestBaseFormationRunQuery(engagementId);
  const run = latest.data ?? null;
  if (!run) return null;
  const note = describeBaseFormationRun(run);
  const inFlight = isRunInFlight(run.status);
  if (!inFlight && !note) return null;
  const empty = !inFlight && note?.tone === 'empty';
  if (empty && !documents) return null;
  const nothing = empty && documents ? nothingToReadNote(documents) : null;

  return (
    <AiSurface as="div" role="status" className={cn('rounded-card p-4', className)}>
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex h-9 w-9 flex-none items-center justify-center rounded-card bg-ai-surface-border text-ai-ink">
          {inFlight ? (
            <Spinner className="h-4 w-4" />
          ) : note?.tone === 'failed' ? (
            <AlertTriangle className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Sparkles className="h-4 w-4" aria-hidden="true" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-body-sm font-semibold text-ai-ink">
            {inFlight
              ? run.status === 'queued'
                ? 'AI extraction is waiting to start'
                : progressLabel('Reading the contract documents', run.progress ?? null, 'section')
              : note?.tone === 'degraded'
                ? 'AI extraction finished with sections skipped'
                : nothing
                  ? nothing.title
                  : 'AI extraction failed'}
          </div>
          {note ? (
            <div
              className={cn(
                'mt-1 text-meta',
                note.tone === 'failed' ? 'text-posture-critical' : 'text-ink-700',
              )}
            >
              {nothing ? nothing.message : note.message}
            </div>
          ) : null}
        </div>
      </div>
    </AiSurface>
  );
}
